import type {
  AttachmentDetailsInput,
  AuditLogPageDTO,
  BlazeCheckoutKind,
  BlazeGiftPreviewDTO,
  BlazeSaleDTO,
  BlazeStatusDTO,
  AutoModInput,
  AutoModSettingsDTO,
  BanDTO,
  BootstrapDTO,
  CategoryDTO,
  ChannelDTO,
  ConversationDTO,
  CreateChannelInput,
  CreateEventInput,
  CreatePollInput,
  CreateReportInput,
  CreateSignupInviteInput,
  EmojiDTO,
  ForumPostDTO,
  ForumPostListDTO,
  FriendDTO,
  FriendRequestDTO,
  FriendsDTO,
  GrantableBadgeId,
  IceServerConfig,
  InviteDTO,
  InvitePreviewDTO,
  LinkPreviewDTO,
  LoginInput,
  MemberDTO,
  MessageDTO,
  MessagePageDTO,
  MessageTargetRef,
  MessageVersionDTO,
  OAuthAccountDTO,
  OAuthProviderId,
  OAuthStartInput,
  NotificationDTO,
  NotificationLevel,
  OfficialChannelsDTO,
  OfficialPostsDTO,
  PublicAuthConfigDTO,
  PublicUser,
  RegisterInput,
  ReorderChannelsInput,
  ReportDTO,
  ReportPageDTO,
  ReportStatus,
  ResolveReportInput,
  RoleDTO,
  SavedMessageDTO,
  ScheduledPostDTO,
  SearchResultDTO,
  SelfUser,
  ServerDTO,
  ServerEventDTO,
  ServerSoundDTO,
  GettingStartedDTO,
  ForwardMessageInput,
  SessionDTO,
  SignupInviteDTO,
  StaffActionDTO,
  StaffStatsDTO,
  StaffUserDTO,
  StaffUserPageDTO,
  ThreadDTO,
  ThreadListDTO,
  TwoFactorChallengeDTO,
  TwoFactorRecoveryCodesDTO,
  TwoFactorSetupDTO,
  TwoFactorStatusDTO,
  UpdateEventInput,
  UpdateProfileInput,
  UpdateServerInput,
  UpdateSettingsInput,
  UserProfileDTO,
  UserSettingsDTO,
} from '@clanfire/shared';
import { http, toQuery } from './client';

type AuthResponse = { user: SelfUser; settings: UserSettingsDTO };

export const authApi = {
  me: () => http.get<AuthResponse>('/auth/me', { allowUnauthorized: true }),
  /** Logs in, or asks for a code from the authenticator app (two-factor sign-in). */
  login: (input: LoginInput) => http.post<AuthResponse | TwoFactorChallengeDTO>('/auth/login', input, { allowUnauthorized: true }),
  loginTwoFactor: (ticket: string, code: string) => http.post<AuthResponse>('/auth/login/two-factor', { ticket, code }, { allowUnauthorized: true }),
  register: (input: RegisterInput) => http.post<AuthResponse>('/auth/register', input),
  config: () => http.get<PublicAuthConfigDTO>('/auth/config', { allowUnauthorized: true }),
  verifyEmail: (token: string) => http.post<{ ok: true; sameAccount: boolean }>('/auth/verify-email', { token }, { allowUnauthorized: true }),
  resendVerification: () => http.post<{ ok: true }>('/auth/resend-verification'),
  logout: () => http.post<void>('/auth/logout', undefined, { allowUnauthorized: true }),
  forgotPassword: (email: string, captchaToken?: string) => http.post<{ ok: true }>('/auth/forgot-password', { email, captchaToken }),
  /** Emails the signed-in account a link to set (or reset) its password. */
  sendPasswordLink: () => http.post<{ ok: true }>('/auth/password-link'),
  resetPassword: (token: string, password: string) => http.post<void>('/auth/reset-password', { token, password }),
  changePassword: (currentPassword: string, newPassword: string) => http.post<void>('/auth/change-password', { currentPassword, newPassword }),
  sessions: () => http.get<SessionDTO[]>('/auth/sessions'),
  revokeSession: (id: string) => http.delete<void>(`/auth/sessions/${id}`),
  revokeOtherSessions: () => http.post<{ revoked: number }>('/auth/sessions/revoke-others'),
  twoFactor: {
    status: () => http.get<TwoFactorStatusDTO>('/auth/two-factor'),
    setup: (password: string) => http.post<TwoFactorSetupDTO>('/auth/two-factor/setup', { password }),
    enable: (code: string) => http.post<TwoFactorRecoveryCodesDTO & { signedOut: number }>('/auth/two-factor/enable', { code }),
    disable: (password: string, code: string) => http.post<void>('/auth/two-factor/disable', { password, code }),
    newRecoveryCodes: (code: string) => http.post<TwoFactorRecoveryCodesDTO>('/auth/two-factor/recovery-codes', { code }),
  },
  /** Sign in with Google or GitHub: answers with the provider's page to send the browser to. */
  oauthStart: (provider: OAuthProviderId, input: OAuthStartInput) =>
    http.post<{ url: string }>(`/auth/oauth/${provider}/start`, input, { allowUnauthorized: true }),
  oauthAccounts: () => http.get<OAuthAccountDTO[]>('/auth/oauth/accounts'),
  oauthUnlink: (provider: OAuthProviderId) => http.delete<void>(`/auth/oauth/accounts/${provider}`),
};

export const eventApi = {
  list: (serverId: string) => http.get<ServerEventDTO[]>(`/servers/${serverId}/events`),
  create: (serverId: string, input: CreateEventInput) => http.post<ServerEventDTO>(`/servers/${serverId}/events`, input),
  update: (serverId: string, eventId: string, input: UpdateEventInput) => http.patch<ServerEventDTO>(`/servers/${serverId}/events/${eventId}`, input),
  remove: (serverId: string, eventId: string) => http.delete<void>(`/servers/${serverId}/events/${eventId}`),
  setInterested: (serverId: string, eventId: string, on: boolean) =>
    on ? http.put<ServerEventDTO>(`/servers/${serverId}/events/${eventId}/interested`) : http.delete<ServerEventDTO>(`/servers/${serverId}/events/${eventId}/interested`),
  interested: (serverId: string, eventId: string) => http.get<PublicUser[]>(`/servers/${serverId}/events/${eventId}/interested`),
};

export const bootstrapApi = {
  load: () => http.get<BootstrapDTO>('/bootstrap'),
};

export const userApi = {
  /** The welcome tour has been seen; the getting-started checklist hidden or shown again. */
  gettingStarted: (input: { tourSeen?: true; checklistHidden?: boolean }) => http.patch<GettingStartedDTO>('/users/@me/getting-started', input),
  updateProfile: (input: UpdateProfileInput) => http.patch<SelfUser>('/users/@me', input),
  uploadAvatar: (file: Blob, name = 'avatar.png') => {
    const form = new FormData();
    form.append('avatar', file, name);
    return http.put<SelfUser>('/users/@me/avatar', form);
  },
  removeAvatar: () => http.delete<SelfUser>('/users/@me/avatar'),
  uploadBanner: (file: Blob, name = 'banner.png') => {
    const form = new FormData();
    form.append('banner', file, name);
    return http.put<SelfUser>('/users/@me/banner', form);
  },
  removeBanner: () => http.delete<SelfUser>('/users/@me/banner'),
  updateSettings: (input: UpdateSettingsInput) => http.patch<UserSettingsDTO>('/users/@me/settings', input),
  deleteAccount: (password: string) => http.delete<void>('/users/@me', { password }),
  /** "Download my data" as a JSON file. */
  iceServers: () => http.get<{ iceServers: IceServerConfig[] }>('/users/@me/ice-servers'),
  exportData: async () => new Blob([JSON.stringify(await http.get<unknown>('/users/@me/export'), null, 2)], { type: 'application/json' }),
  profile: (userId: string) => http.get<UserProfileDTO>(`/users/${userId}/profile`),
  blocks: () => http.get<PublicUser[]>('/users/@me/blocks'),
  setServerOrder: (serverIds: string[]) => http.put<{ serverIds: string[] }>('/users/@me/server-order', { serverIds }),
  block: (userId: string) => http.put<void>(`/users/${userId}/block`),
  unblock: (userId: string) => http.delete<void>(`/users/${userId}/block`),
};

export const serverApi = {
  create: (name: string, icon?: Blob | null) => {
    const form = new FormData();
    form.append('name', name);
    if (icon) form.append('icon', icon, 'icon.png');
    return http.post<ServerDTO>('/servers', form);
  },
  get: (id: string) => http.get<ServerDTO>(`/servers/${id}`),
  autoMod: (id: string) => http.get<AutoModSettingsDTO>(`/servers/${id}/automod`),
  updateAutoMod: (id: string, input: AutoModInput) => http.put<AutoModSettingsDTO>(`/servers/${id}/automod`, input),
  update: (id: string, input: UpdateServerInput) => http.patch<ServerDTO>(`/servers/${id}`, input),
  acceptRules: (id: string) => http.post<{ rulesAcceptedAt: string | null }>(`/servers/${id}/rules/accept`),
  setIcon: (id: string, icon: Blob) => {
    const form = new FormData();
    form.append('icon', icon, 'icon.png');
    return http.put<ServerDTO>(`/servers/${id}/icon`, form);
  },
  removeIcon: (id: string) => http.delete<ServerDTO>(`/servers/${id}/icon`),
  /** The banner over the channel list (from the Spark level). */
  setBanner: (id: string, banner: Blob) => {
    const form = new FormData();
    form.append('banner', banner, 'banner.png');
    return http.put<ServerDTO>(`/servers/${id}/banner`, form);
  },
  removeBanner: (id: string) => http.delete<ServerDTO>(`/servers/${id}/banner`),
  /** Puts one of your Stokes on the server. */
  stoke: (id: string) => http.post<ServerDTO>(`/servers/${id}/stokes`),
  /** Your profile just for this server (Blaze). */
  setProfileBio: (id: string, bio: string | null) => http.patch<MemberDTO>(`/servers/${id}/members/@me/profile`, { bio }),
  setProfileImage: (id: string, kind: 'avatar' | 'banner', file: Blob) => {
    const form = new FormData();
    form.append(kind, file, `${kind}.png`);
    return http.put<MemberDTO>(`/servers/${id}/members/@me/${kind}`, form);
  },
  removeProfileImage: (id: string, kind: 'avatar' | 'banner') => http.delete<MemberDTO>(`/servers/${id}/members/@me/${kind}`),
  resetProfile: (id: string) => http.delete<MemberDTO>(`/servers/${id}/members/@me/profile`),
  remove: (id: string) => http.delete<void>(`/servers/${id}`),
  leave: (id: string) => http.delete<void>(`/servers/${id}/members/@me`),
  transfer: (id: string, userId: string) => http.post<ServerDTO>(`/servers/${id}/transfer`, { userId }),
  members: (id: string) => http.get<MemberDTO[]>(`/servers/${id}/members`),
  /** Give or take away a role; `userId` can be `@me`. */
  addMemberRole: (id: string, userId: string, roleId: string) => http.put<MemberDTO>(`/servers/${id}/members/${userId}/roles/${roleId}`),
  removeMemberRole: (id: string, userId: string, roleId: string) => http.delete<MemberDTO>(`/servers/${id}/members/${userId}/roles/${roleId}`),
  kick: (id: string, userId: string, reason?: string) => http.delete<void>(`/servers/${id}/members/${userId}`, reason ? { reason } : undefined),
  ban: (id: string, userId: string, reason?: string) => http.put<void>(`/servers/${id}/bans/${userId}`, { reason }),
  timeout: (id: string, userId: string, durationSeconds: number, reason?: string) =>
    http.put<MemberDTO>(`/servers/${id}/members/${userId}/timeout`, { durationSeconds, ...(reason ? { reason } : {}) }),
  removeTimeout: (id: string, userId: string) => http.delete<MemberDTO>(`/servers/${id}/members/${userId}/timeout`),
  unban: (id: string, userId: string) => http.delete<void>(`/servers/${id}/bans/${userId}`),
  bans: (id: string) => http.get<BanDTO[]>(`/servers/${id}/bans`),
  auditLog: (id: string, params: { actorId?: string; action?: string; before?: string } = {}) =>
    http.get<AuditLogPageDTO>(`/servers/${id}/audit-log${toQuery(params)}`),
  setNotificationLevel: (id: string, notificationLevel: NotificationLevel) =>
    http.patch<void>(`/servers/${id}/members/@me/settings`, { notificationLevel }),
  roles: (id: string) => http.get<RoleDTO[]>(`/servers/${id}/roles`),
  createRole: (id: string, input: { name: string; color: string | null; permissions: number; hoist?: boolean; mentionable?: boolean }) =>
    http.post<RoleDTO>(`/servers/${id}/roles`, input),
  updateRole: (
    id: string,
    roleId: string,
    input: {
      name?: string;
      color?: string | null;
      permissions?: number;
      hoist?: boolean;
      mentionable?: boolean;
      selfAssignable?: boolean;
      description?: string | null;
      emoji?: string | null;
    },
  ) =>
    http.patch<RoleDTO>(`/servers/${id}/roles/${roleId}`, input),
  moveRole: (id: string, roleId: string, direction: 'up' | 'down') => http.post<RoleDTO[]>(`/servers/${id}/roles/${roleId}/move`, { direction }),
  deleteRole: (id: string, roleId: string) => http.delete<void>(`/servers/${id}/roles/${roleId}`),
  createChannel: (id: string, input: { name: string; type: CreateChannelInput['type']; topic?: string | null; categoryId?: string | null }) =>
    http.post<ChannelDTO>(`/servers/${id}/channels`, input),
  setNickname: (id: string, userId: string, nickname: string | null) => http.put<MemberDTO>(`/servers/${id}/members/${userId}/nickname`, { nickname }),
  createCategory: (id: string, name: string) => http.post<CategoryDTO>(`/servers/${id}/categories`, { name }),
  setLayout: (id: string, layout: ReorderChannelsInput) => http.put<ReorderChannelsInput>(`/servers/${id}/layout`, layout),
  invites: (id: string) => http.get<InviteDTO[]>(`/servers/${id}/invites`),
  /** Pick your roles: give yourself (or drop) a role the server lets members pick. */
  pickRole: (serverId: string, roleId: string, on: boolean) =>
    on ? http.put<MemberDTO>(`/servers/${serverId}/my-roles/${roleId}`) : http.delete<MemberDTO>(`/servers/${serverId}/my-roles/${roleId}`),
  /** The soundboard's own sounds. */
  sounds: (serverId: string) => http.get<ServerSoundDTO[]>(`/servers/${serverId}/sounds`),
  addSound: (serverId: string, input: { name: string; emoji?: string; file: File }) => {
    const form = new FormData();
    form.append('name', input.name);
    if (input.emoji) form.append('emoji', input.emoji);
    form.append('file', input.file, input.file.name);
    return http.post<ServerSoundDTO>(`/servers/${serverId}/sounds`, form);
  },
  deleteSound: (serverId: string, soundId: string) => http.delete<void>(`/servers/${serverId}/sounds/${soundId}`),
  /** The server's custom invite link (null removes it). */
  setVanity: (id: string, code: string | null) => http.put<{ invite: InviteDTO | null }>(`/servers/${id}/vanity`, { code }),
  createInvite: (id: string, input: { maxUses: number | null; expiresInSeconds: number | null }) => http.post<InviteDTO>(`/servers/${id}/invites`, input),
};

export const emojiApi = {
  create: (serverId: string, name: string, image: File) => {
    const form = new FormData();
    form.append('name', name);
    form.append('image', image);
    return http.post<EmojiDTO>(`/servers/${serverId}/emojis`, form);
  },
  rename: (serverId: string, emojiId: string, name: string) => http.patch<EmojiDTO>(`/servers/${serverId}/emojis/${emojiId}`, { name }),
  remove: (serverId: string, emojiId: string) => http.delete<void>(`/servers/${serverId}/emojis/${emojiId}`),
};

export const pollApi = {
  create: (target: MessageTargetRef, input: CreatePollInput) =>
    http.post<MessageDTO>(target.channelId ? `/channels/${target.channelId}/polls` : `/conversations/${target.conversationId}/polls`, input),
  vote: (messageId: string, optionIds: string[]) => http.put<MessageDTO>(`/messages/${messageId}/poll/votes`, { optionIds }),
  end: (messageId: string) => http.post<MessageDTO>(`/messages/${messageId}/poll/end`),
};

export const channelApi = {
  update: (
    id: string,
    input: { name?: string; topic?: string | null; position?: number; categoryId?: string | null; slowModeSeconds?: number; forumTags?: Array<{ id?: string; name: string; emoji: string | null }> },
  ) => http.patch<ChannelDTO>(`/channels/${id}`, input),
  remove: (id: string) => http.delete<void>(`/channels/${id}`),
  /** Mute or unmute a channel for yourself. */
  mute: (id: string, muted: boolean) => http.put<{ muted: boolean }>(`/channels/${id}/mute`, { muted }),
  /** A role's permission override in the channel (allow and deny both 0 removes it). */
  setOverwrite: (id: string, roleId: string, input: { allow: number; deny: number }) => http.put<ChannelDTO>(`/channels/${id}/overwrites/${roleId}`, input),
};

export const categoryApi = {
  rename: (id: string, name: string) => http.patch<CategoryDTO>(`/categories/${id}`, { name }),
  remove: (id: string) => http.delete<void>(`/categories/${id}`),
};

function targetPath(target: MessageTargetRef) {
  if (target.channelId) return `/channels/${target.channelId}`;
  if (target.conversationId) return `/conversations/${target.conversationId}`;
  throw new Error('Message target required');
}

export const messageApi = {
  list: (target: MessageTargetRef, params: { before?: string; after?: string; around?: string; limit?: number }, signal?: AbortSignal) =>
    http.get<MessagePageDTO>(`${targetPath(target)}/messages${toQuery(params)}`, { signal }),
  send: (
    target: MessageTargetRef,
    input: { content: string; replyToId?: string | null; replyPing?: boolean; nonce: string; files?: File[]; attachments?: AttachmentDetailsInput[]; voice?: { durationMs: number; waveform: string } },
  ) => {
    const replyPing = input.replyPing ?? true;
    if (input.files?.length) {
      const form = new FormData();
      form.append('content', input.content);
      form.append('nonce', input.nonce);
      if (input.voice) {
        form.append('voiceDurationMs', String(input.voice.durationMs));
        form.append('voiceWaveform', input.voice.waveform);
      }
      if (input.replyToId) {
        form.append('replyToId', input.replyToId);
        form.append('replyPing', String(replyPing));
      }
      for (const file of input.files) form.append('files', file, file.name);
      if (input.attachments?.some((a) => a.spoiler || a.description)) form.append('attachments', JSON.stringify(input.attachments));
      return http.post<MessageDTO>(`${targetPath(target)}/messages`, form);
    }
    return http.post<MessageDTO>(`${targetPath(target)}/messages`, { content: input.content, replyToId: input.replyToId ?? null, replyPing, nonce: input.nonce });
  },
  edit: (id: string, content: string) => http.patch<MessageDTO>(`/messages/${id}`, { content }),
  remove: (id: string) => http.delete<void>(`/messages/${id}`),
  react: (id: string, emoji: string) => http.put<void>(`/messages/${id}/reactions/${encodeURIComponent(emoji)}`),
  unreact: (id: string, emoji: string) => http.delete<void>(`/messages/${id}/reactions/${encodeURIComponent(emoji)}`),
  ack: (target: MessageTargetRef) => http.post<{ lastReadAt: string }>(`${targetPath(target)}/ack`),
  pins: (target: MessageTargetRef) => http.get<{ messages: MessageDTO[] }>(`${targetPath(target)}/pins`),
  /** Forwards a message to a channel or conversation, with an optional comment. */
  forward: (id: string, input: Partial<ForwardMessageInput>) => http.post<MessageDTO>(`/messages/${id}/forward`, input),
  /** What an edited message said before (for its author and moderators). */
  versions: (id: string) => http.get<MessageVersionDTO[]>(`/messages/${id}/versions`),
  pin: (id: string) => http.put<MessageDTO>(`/messages/${id}/pin`),
  unpin: (id: string) => http.delete<MessageDTO>(`/messages/${id}/pin`),
};

export const staffApi = {
  stats: () => http.get<StaffStatsDTO>('/staff/stats'),
  users: (params: { q?: string; filter: 'all' | 'staff' | 'suspended'; cursor?: string }) => http.get<StaffUserPageDTO>(`/staff/users${toQuery(params)}`),
  suspend: (userId: string, reason: string) => http.post<StaffUserDTO>(`/staff/users/${userId}/suspend`, { reason }),
  unsuspend: (userId: string) => http.delete<StaffUserDTO>(`/staff/users/${userId}/suspend`),
  setRank: (userId: string, rank: 'ADMIN' | 'MODERATOR' | null) => http.put<StaffUserDTO>(`/staff/users/${userId}/rank`, { rank }),
  setBadge: (userId: string, badge: GrantableBadgeId, on: boolean) =>
    on ? http.put<StaffUserDTO>(`/staff/users/${userId}/badges/${badge}`) : http.delete<StaffUserDTO>(`/staff/users/${userId}/badges/${badge}`),
  actions: () => http.get<StaffActionDTO[]>('/staff/actions'),
  invites: () => http.get<SignupInviteDTO[]>('/staff/invites'),
  createInvite: (input: CreateSignupInviteInput) => http.post<SignupInviteDTO>('/staff/invites', input),
  revokeInvite: (inviteId: string) => http.delete<SignupInviteDTO>(`/staff/invites/${inviteId}`),
  reports: (params: { status: ReportStatus; cursor?: string }) => http.get<ReportPageDTO>(`/staff/reports${toQuery(params)}`),
  resolveReport: (reportId: string, input: ResolveReportInput) => http.post<ReportDTO>(`/staff/reports/${reportId}`, input),
  officialChannels: () => http.get<OfficialChannelsDTO>('/staff/official-channels'),
  officialPosts: () => http.get<OfficialPostsDTO>('/staff/official-messages'),
  /** Posts now, or schedules it for `sendAt` (an ISO date). */
  postOfficial: (channelId: string, content: string, sendAt?: string) =>
    http.post<MessageDTO | ScheduledPostDTO>('/staff/official-messages', { channelId, content, ...(sendAt && { sendAt }) }),
  editOfficial: (messageId: string, content: string) => http.patch<MessageDTO>(`/staff/official-messages/${messageId}`, { content }),
  deleteOfficial: (messageId: string) => http.delete<void>(`/staff/official-messages/${messageId}`),
  cancelScheduled: (postId: string) => http.delete<void>(`/staff/scheduled-posts/${postId}`),
  giveBlaze: (userId: string, days: number) => http.put<{ blazeUntil: string | null }>(`/staff/users/${userId}/blaze`, { days }),
  removeBlaze: (userId: string) => http.delete<{ blazeUntil: string | null }>(`/staff/users/${userId}/blaze`),
  blazeSale: () => http.get<BlazeSaleDTO>('/staff/blaze-sale'),
  setBlazeSale: (onSale: boolean) => http.put<BlazeSaleDTO>('/staff/blaze-sale', { onSale }),
};

/** ClanFire Blaze: its page, Paddle checkouts, Founder months, gifts and Stokes. */
export const blazeApi = {
  status: () => http.get<BlazeStatusDTO>('/blaze'),
  /** `giftTo`: a gift for someone in particular, sent to them by DM once it's paid for. */
  checkout: (kind: BlazeCheckoutKind, giftTo?: string) => http.post<{ transactionId: string }>('/blaze/checkout', { kind, giftTo }),
  manage: () => http.post<{ url: string }>('/blaze/manage'),
  keep: () => http.post<BlazeStatusDTO>('/blaze/keep'),
  claimFounder: () => http.post<BlazeStatusDTO>('/blaze/founder'),
  gift: (code: string) => http.get<BlazeGiftPreviewDTO>(`/blaze/gifts/${encodeURIComponent(code)}`),
  redeem: (code: string) => http.post<BlazeStatusDTO>(`/blaze/gifts/${encodeURIComponent(code)}/redeem`),
  removeStoke: (stokeId: string) => http.delete<BlazeStatusDTO>(`/blaze/stokes/${stokeId}`),
  /** The sounds of all your servers (with Blaze). */
  sounds: () => http.get<ServerSoundDTO[]>('/blaze/sounds'),
};

export const reportApi = {
  create: (input: CreateReportInput) => http.post<{ id: string }>('/reports', input),
};

export const pushApi = {
  subscribe: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) => http.post<void>('/push/subscriptions', subscription),
  unsubscribe: (endpoint: string) => http.delete<void>('/push/subscriptions', { endpoint }),
};

export const embedApi = {
  /** Resolves to undefined when the page has nothing to preview (204). */
  preview: (url: string) => http.get<LinkPreviewDTO | undefined>(`/embeds${toQuery({ url })}`),
};

export const threadApi = {
  list: (channelId: string) => http.get<ThreadListDTO>(`/channels/${channelId}/threads`),
  /** On a message in the channel (messageId), or on its own. */
  create: (channelId: string, input: { name: string; messageId?: string }) => http.post<ThreadDTO>(`/channels/${channelId}/threads`, input),
  get: (threadId: string) => http.get<ThreadDTO>(`/threads/${threadId}`),
  update: (threadId: string, input: { name?: string; archived?: boolean; tagIds?: string[] }) => http.patch<ThreadDTO>(`/threads/${threadId}`, input),
  remove: (threadId: string) => http.delete<void>(`/threads/${threadId}`),
};

export const forumApi = {
  posts: (forumId: string) => http.get<ForumPostListDTO>(`/channels/${forumId}/posts`),
  create: (forumId: string, input: { title: string; content: string; tagIds: string[] }) => http.post<ForumPostDTO>(`/channels/${forumId}/posts`, input),
};

export const conversationApi = {
  list: () => http.get<ConversationDTO[]>('/conversations'),
  get: (id: string) => http.get<ConversationDTO>(`/conversations/${id}`),
  open: (userId: string) => http.post<ConversationDTO>('/conversations', { userId }),
  close: (id: string) => http.delete<void>(`/conversations/${id}`),
  createGroup: (userIds: string[], name?: string | null) => http.post<ConversationDTO>('/conversations/groups', { userIds, ...(name ? { name } : {}) }),
  updateGroup: (id: string, input: { name?: string | null; ownerId?: string }) => http.patch<ConversationDTO>(`/conversations/${id}`, input),
  addMembers: (id: string, userIds: string[]) => http.post<ConversationDTO>(`/conversations/${id}/members`, { userIds }),
  /** Removes someone from a group, or leaves it when it's your own id. */
  removeMember: (id: string, userId: string) => http.delete<void>(`/conversations/${id}/members/${userId}`),
};

export const friendApi = {
  list: () => http.get<FriendsDTO>('/friends'),
  request: (username: string) =>
    http.post<{ status: 'sent' | 'accepted'; request?: FriendRequestDTO; friend?: FriendDTO }>('/friends/requests', { username }),
  accept: (requestId: string) => http.post<FriendDTO>(`/friends/requests/${requestId}/accept`),
  decline: (requestId: string) => http.delete<void>(`/friends/requests/${requestId}`),
  remove: (userId: string) => http.delete<void>(`/friends/${userId}`),
};

/** The Feedback button: goes to the team, with an optional screenshot. */
export const feedbackApi = {
  send: (input: { kind: 'bug' | 'idea' | 'other'; message: string; page: string; screen: string; screenshot?: File | null }) => {
    const form = new FormData();
    form.append('kind', input.kind);
    form.append('message', input.message);
    form.append('page', input.page);
    form.append('screen', input.screen);
    if (input.screenshot) form.append('screenshot', input.screenshot, input.screenshot.name);
    return http.post<{ ok: true }>('/feedback', form);
  },
};

export const savedApi = {
  list: () => http.get<SavedMessageDTO[]>('/saved'),
  save: (messageId: string, remindAt: string | null = null) => http.put<{ messageId: string; saved: true; remindAt: string | null }>(`/saved/${messageId}`, { remindAt }),
  unsave: (messageId: string) => http.delete<void>(`/saved/${messageId}`),
};

export const inviteApi = {
  preview: (code: string) => http.get<InvitePreviewDTO>(`/invites/${encodeURIComponent(code)}`),
  accept: (code: string) => http.post<ServerDTO>(`/invites/${encodeURIComponent(code)}/accept`),
  revoke: (code: string) => http.delete<void>(`/invites/${encodeURIComponent(code)}`),
};

export const notificationApi = {
  list: (before?: string) => http.get<{ notifications: NotificationDTO[]; unreadCount: number }>(`/notifications${toQuery({ before })}`),
  markRead: (ids: string[]) => http.post<void>('/notifications/read', { ids }),
  markAllRead: () => http.post<void>('/notifications/read-all'),
};

export interface SearchParams {
  q?: string;
  serverId?: string;
  channelId?: string;
  conversationId?: string;
  authorId?: string;
  after?: string;
  before?: string;
  has?: 'attachment' | 'image' | 'link';
  offset?: number;
}

export const searchApi = {
  search: (params: SearchParams, signal?: AbortSignal) =>
    http.get<SearchResultDTO>(`/search${toQuery(params as Record<string, string | number | undefined>)}`, { signal }),
};
